import { Link } from "react-router";
import { Button } from "@rekati/ui";
import IconArrow from "~icons/solar/arrow-right-linear";
import IconCalendar from "~icons/solar/calendar-minimalistic-linear";
import IconMap from "~icons/solar/map-point-hospital-linear";
import IconOpen from "~icons/solar/square-arrow-right-up-linear";
import { useI18n } from "../../content/i18n";
import { Countdown } from "./Countdown";
import type { EventFacts } from "./queries";

/** "29.10.26" in Jakarta time, for the stub's punch line. */
function shortDate(iso: string) {
  const parts = new Intl.DateTimeFormat("en-GB", {
    timeZone: "Asia/Jakarta",
    day: "2-digit",
    month: "2-digit",
    year: "2-digit",
  }).formatToParts(new Date(iso));
  const get = (type: string) => parts.find((p) => p.type === type)?.value ?? "";
  return `${get("day")}.${get("month")}.${get("year")}`;
}

/**
 * The facts a person registers on, as an admission ticket: a yellow header
 * strip, when and where, the countdown, a perforation, and the one action.
 */
export function TicketStub({ facts }: { facts: EventFacts }) {
  const { t, formatDate, formatTime } = useI18n();
  return (
    <div className="relative overflow-hidden rounded-3xl border border-border bg-card shadow-soft">
      <div className="flex items-center justify-between bg-volt px-5 py-2.5 font-mono text-xs font-medium tracking-[0.16em] text-volt-foreground uppercase">
        <span>{t("hero.admit")}</span>
        <span className="tabular-nums">{shortDate(facts.startsAt)}</span>
      </div>
      <div className="grid gap-5 p-5 sm:p-6">
        <dl className="grid gap-4">
          <div className="grid grid-cols-[36px_1fr] gap-x-3">
            <span
              className="row-span-2 grid size-9 place-items-center rounded-full bg-paint-blue text-white"
              aria-hidden="true"
            >
              <IconCalendar className="size-[18px]" />
            </span>
            <dt className="font-mono text-[0.68rem] tracking-[0.16em] text-muted-foreground uppercase">
              {t("hero.when")}
            </dt>
            <dd className="text-base leading-snug font-semibold">
              {formatDate(facts.startsAt)}
              <span className="block font-normal text-muted-foreground tabular-nums">
                {t("hero.time", { start: formatTime(facts.startsAt), end: formatTime(facts.endsAt) })}
              </span>
            </dd>
          </div>
          <div className="grid grid-cols-[36px_1fr] gap-x-3">
            <span
              className="row-span-2 grid size-9 place-items-center rounded-full bg-paint-pink text-white"
              aria-hidden="true"
            >
              <IconMap className="size-[18px]" />
            </span>
            <dt className="font-mono text-[0.68rem] tracking-[0.16em] text-muted-foreground uppercase">
              {t("hero.where")}
            </dt>
            <dd className="text-base leading-snug font-semibold">
              {facts.venueName}
              <span className="block font-normal text-muted-foreground">
                {facts.venueAddress ?? t("hero.tbc")}
              </span>
              {facts.mapUrl ? (
                <a
                  href={facts.mapUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="mt-1.5 inline-flex items-center gap-1.5 text-sm font-medium underline decoration-volt decoration-[3px] underline-offset-4"
                >
                  {t("hero.openMap")}
                  <IconOpen className="size-4" aria-hidden="true" />
                </a>
              ) : null}
            </dd>
          </div>
        </dl>
        <Countdown to={facts.startsAt} />
      </div>
      {/* The perforation: two notches cut out of the card and a dashed tear line. */}
      <div className="relative h-0" aria-hidden="true">
        <span className="absolute top-1/2 -left-3.5 size-7 -translate-y-1/2 rounded-full border border-border bg-background" />
        <span className="absolute top-1/2 -right-3.5 size-7 -translate-y-1/2 rounded-full border border-border bg-background" />
        <span className="absolute inset-x-6 top-0 border-t-2 border-dashed border-border" />
      </div>
      <div className="grid gap-2 p-5 sm:p-6">
        <Button asChild variant="volt" size="xl" className="w-full justify-between">
          <Link to="/register">
            {t("hero.register")}
            <IconArrow aria-hidden="true" />
          </Link>
        </Button>
        <Link
          to="/rsvp"
          className="justify-self-center rounded-sm py-1 text-sm text-muted-foreground underline-offset-4 hover:text-foreground hover:underline"
        >
          {t("hero.signIn")}
        </Link>
      </div>
    </div>
  );
}
